import { useMemo } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, HelpCircle } from "lucide-react";

/**
 * Author-side questionnaire editor.
 *
 * `value` is a list of `{id, prompt, order}`. Callers pass a controlled
 * setter. We keep the shape stable so the API can round-trip without
 * transforming it: the backend only re-indexes `order` and generates
 * missing ids.
 *
 * Kept intentionally minimal: no drag-drop, no rich text, no branching.
 * Just add / edit / delete / reorder.
 */
export function QuestionnaireEditor({ value, onChange, disabled, testidPrefix = "questionnaire" }) {
  const items = Array.isArray(value) ? value : [];

  const update = (idx, patch) => {
    const next = items.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    onChange(next);
  };
  const add = () => {
    const nextId = `new_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    onChange([...items, { id: nextId, prompt: "", order: items.length }]);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next.map((q, i) => ({ ...q, order: i })));
  };

  return (
    <div className="rounded-2xl border border-border/70 bg-card p-4" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-start gap-2 mb-3">
        <HelpCircle size={16} className="text-[hsl(var(--sb-gold-2))] mt-0.5" />
        <div>
          <div className="text-sm font-semibold">Feedback Questionnaire</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Ask your accepted beta readers specific questions. Only you and accepted readers see these.
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="text-xs text-muted-foreground italic mb-2" data-testid={`${testidPrefix}-empty`}>
          No questions yet.
        </div>
      )}

      <ul className="space-y-2">
        {items.map((q, idx) => (
          <li key={q.id || idx} className="flex items-start gap-2" data-testid={`${testidPrefix}-item-${idx}`}>
            <div className="mt-2 text-[11px] text-muted-foreground font-mono w-6 text-right shrink-0">{idx + 1}.</div>
            <textarea
              rows={2}
              maxLength={500}
              value={q.prompt || ""}
              disabled={disabled}
              onChange={(e) => update(idx, { prompt: e.target.value })}
              placeholder="e.g. Where did the pacing drag?"
              className="sb-input flex-1 resize-none text-sm"
              data-testid={`${testidPrefix}-input-${idx}`}
            />
            <div className="flex flex-col gap-1 shrink-0">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={disabled || idx === 0}
                title="Move up"
                aria-label="Move up"
                className="p-1 rounded border border-border hover:bg-muted disabled:opacity-40"
                data-testid={`${testidPrefix}-up-${idx}`}
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={disabled || idx === items.length - 1}
                title="Move down"
                aria-label="Move down"
                className="p-1 rounded border border-border hover:bg-muted disabled:opacity-40"
                data-testid={`${testidPrefix}-down-${idx}`}
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                title="Delete"
                aria-label="Delete question"
                className="p-1 rounded border border-[hsl(var(--sb-clay))]/40 text-[hsl(var(--sb-clay))] hover:bg-[hsl(var(--sb-clay))]/10 disabled:opacity-40"
                data-testid={`${testidPrefix}-delete-${idx}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={add}
        disabled={disabled || items.length >= 40}
        className="mt-3 inline-flex items-center gap-1.5 text-sm rounded-full border border-[hsl(var(--sb-gold))]/60 px-3 py-1.5 hover:bg-[hsl(var(--sb-gold))]/10 disabled:opacity-40"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus size={14} /> Add question
      </button>
      {items.length >= 40 && (
        <span className="ml-2 text-[11px] text-muted-foreground">Max 40 questions.</span>
      )}
    </div>
  );
}

/**
 * Read-only view of a questionnaire — used for accepted readers, and
 * for the author's review of a single reader's submitted answers.
 */
export function QuestionnaireReadOnly({ questionnaire, answersByQid, emptyLabel = "No questions." }) {
  const sorted = useMemo(
    () => (questionnaire || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [questionnaire]
  );
  if (sorted.length === 0) return <div className="text-xs text-muted-foreground italic">{emptyLabel}</div>;
  return (
    <ol className="space-y-3 list-decimal pl-5">
      {sorted.map((q) => (
        <li key={q.id} data-testid={`questionnaire-ro-item-${q.id}`}>
          <div className="text-sm font-medium">{q.prompt}</div>
          {answersByQid && (
            <div
              className="mt-1 text-sm whitespace-pre-wrap break-words rounded border border-border/60 bg-muted/40 p-2"
              data-testid={`questionnaire-ro-answer-${q.id}`}
            >
              {answersByQid[q.id]?.response?.trim() || <span className="text-muted-foreground italic">(no answer)</span>}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export default QuestionnaireEditor;
